// File path: client/src/components/common/Sidebar.jsx

import React from 'react';
import { Link, useLocation, useSearchParams } from 'react-router-dom';
import {
  LayoutDashboard,
  FileText,
  Users,
  Package,
  StickyNote,
  Settings,
  UserCog,
  Menu,
  ChevronLeft,
  Inbox,
  ClipboardCheck,
  Home,
  Wallet
} from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { useNotifications } from '../../context/NotificationContext';

const Sidebar = ({ collapsed, onToggle, activeTab, setActiveTab }) => {
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const { unreadCount } = useNotifications();

  const navigationItems = [
    {
      id: 'dashboard',
      name: 'Dashboard',
      icon: Home,
      path: '/dashboard'
    },
    {
      id: 'workspace',
      name: 'Workspace',
      icon: LayoutDashboard,
      path: '/workspace/table'
    },
    {
      id: 'inbox',
      name: 'Inbox',
      icon: Inbox,
      path: '/inbox'
    },
    {
      id: 'attendance',
      name: 'Attendance',
      icon: ClipboardCheck,
      path: '/attendance',
      // Only employees can access Attendance tab (admins/managers use Team tab)
      roles: ['employee']
    },
    {
      id: 'quotations',
      name: 'Quotations',
      icon: FileText,
      path: '/quotations'
    },
    {
      id: 'clients',
      name: 'Clients',
      icon: Users,
      path: '/clients'
    },
    {
      id: 'products',
      name: 'Products',
      icon: Package,
      path: '/quotations?view=products'
    },
    {
      id: 'notes-reminders',
      name: 'Notes & Reminders',
      icon: StickyNote,
      path: '/notes-reminders'
    },
    {
      id: 'wallet',
      name: 'Wallet',
      icon: Wallet,
      path: '/wallet'
    },
    {
      id: 'users',
      name: 'Team',
      icon: UserCog,
      path: '/users',
      // Super Admin, Admin, and Managers can access Team tab
      roles: ['super_admin', 'admin', 'manager']
    },
    {
      id: 'settings',
      name: 'Settings',
      icon: Settings,
      path: '/settings'
    }
  ];

  // Filter navigation items based on user role
  const filteredNavigationItems = navigationItems.filter(item => {
    // If item has role restrictions, check if user's role is allowed
    if (item.roles && user?.role) {
      // Support both 'manager' and 'admin' roles for Team tab (backward compatibility)
      const allowedRoles = item.id === 'users' ? ['manager', 'admin'] : item.roles;
      return allowedRoles.includes(user.role);
    }
    // If no role restrictions, show to everyone
    return true;
  });

  const isActive = (path, itemId) => {
    // For dashboard, exact match
    if (itemId === 'dashboard') {
      return location.pathname === '/dashboard' || location.pathname === '/';
    }
    // For workspace, check if we're on any workspace route
    if (path === '/workspace') {
      return location.pathname === '/workspace' ||
        location.pathname.startsWith('/workspace/');
    }
    // For quotations page, check view parameter to determine which tab is active
    if (location.pathname.startsWith('/quotations') || location.pathname.startsWith('/products')) {
      const viewParam = searchParams.get('view');
      if (itemId === 'products') {
        // Products is active only when view=products
        return viewParam === 'products';
      } else if (itemId === 'quotations') {
        // Quotations is active only when NOT in products view
        return viewParam !== 'products';
      }
    }
    return location.pathname.startsWith(path);
  };

  const handleNavClick = (itemId, itemPath) => {
    setActiveTab(itemId);
    // If clicking on workspace and already on a workspace sub-route, stay there
    // Otherwise navigate to the main workspace
    if (itemPath === '/workspace/table' && location.pathname.startsWith('/workspace/')) {
      return; // Don't navigate, just update active tab
    }
  };

  return (
    <div className={`bg-white border-r border-gray-200 flex flex-col h-screen transition-all duration-300 ease-in-out ${collapsed ? 'w-16' : 'w-64'
      }`}>
      {/* Header */}
      <div className="p-4 border-b border-gray-200 flex items-center justify-between">
        {!collapsed && (
          <div>
            <h1 className="text-xl font-bold text-primary-700">MEGA</h1>
            <p className="text-xs text-gray-500">Management System</p>
          </div>
        )}
        <button
          onClick={onToggle}
          className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
        >
          {collapsed ? (
            <Menu className="h-5 w-5 text-gray-600" />
          ) : (
            <ChevronLeft className="h-5 w-5 text-gray-600" />
          )}
        </button>
      </div>

      {/* Navigation */}
      <nav className="flex-1 p-4 space-y-2 overflow-y-auto">
        {filteredNavigationItems.map((item) => {
          const Icon = item.icon;
          const active = isActive(item.path, item.id);

          return (
            <Link
              key={item.id}
              to={item.path}
              onClick={() => handleNavClick(item.id, item.path)}
              className={`
                flex items-center p-3 rounded-lg transition-all duration-200 group relative
                ${active
                  ? 'bg-gradient-to-r from-primary-50 to-primary-100 text-primary-700 shadow-sm'
                  : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                }
                ${collapsed ? 'justify-center' : 'justify-start'}
              `}
            >
              <div className="relative">
                <Icon className={`h-5 w-5 flex-shrink-0 ${active ? 'text-primary-600' : ''}`} />
                {item.id === 'inbox' && unreadCount > 0 && collapsed && (
                  <span className="absolute -top-1 -right-1 w-4 h-4 bg-error-500 rounded-full flex items-center justify-center text-white text-xs font-bold">
                    {unreadCount > 9 ? '9+' : unreadCount}
                  </span>
                )}
              </div>

              {!collapsed && (
                <div className="ml-3 flex-1 flex items-center justify-between">
                  <div className="text-sm font-medium">{item.name}</div>
                  {item.id === 'inbox' && unreadCount > 0 && (
                    <span className="inline-flex items-center justify-center px-2 py-0.5 text-xs font-bold text-white bg-error-500 rounded-full">
                      {unreadCount > 99 ? '99+' : unreadCount}
                    </span>
                  )}
                </div>
              )}

              {/* Tooltip for collapsed state */}
              {collapsed && (
                <div className="absolute left-16 bg-gray-900 text-white text-xs rounded-md py-1 px-2 
                               opacity-0 invisible group-hover:opacity-100 group-hover:visible 
                               transition-all duration-200 whitespace-nowrap z-50">
                  {item.name}
                </div>
              )}

              {/* Active indicator */}
              {active && (
                <div className="absolute left-0 top-0 bottom-0 w-1 bg-gradient-to-b from-primary-500 to-primary-600 rounded-r-full" />
              )}
            </Link>
          );
        })}
      </nav>

      {/* Footer */}
      <div className="p-4 border-t border-gray-200 space-y-3 flex-shrink-0">
        {/* User Info */}
        {!collapsed && user && (
          <div className="flex items-center space-x-3 p-2 rounded-lg bg-gray-50">
            {user.profileImage?.url ? (
              <img
                src={user.profileImage.url}
                alt={user.name}
                className="w-8 h-8 rounded-full object-cover flex-shrink-0"
              />
            ) : (
              <div className="w-8 h-8 bg-gradient-to-br from-primary-500 to-primary-600 rounded-full flex items-center justify-center flex-shrink-0">
                <span className="text-white text-xs font-bold">
                  {user.name?.charAt(0).toUpperCase() || 'U'}
                </span>
              </div>
            )}
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-gray-900 truncate">{user.name}</p>
              <p className="text-xs text-gray-500 truncate">{user.email}</p>
              <p className="text-xs font-semibold text-primary-600 truncate capitalize mt-0.5">
                {user.role === 'admin' ? 'Manager' : user.role}
              </p>
            </div>
          </div>
        )}

        {/* Version Info */}
        {!collapsed && (
          <div className="text-xs text-gray-500 text-center">
            <div className="font-medium">MEGA Enterprises</div>
            <div className="mt-1">v1.0.0</div>
          </div>
        )}
        {collapsed && (
          <div className="flex justify-center">
            <div className="w-8 h-8 bg-gradient-to-br from-primary-500 to-primary-600 rounded-full flex items-center justify-center">
              <span className="text-white text-xs font-bold">M</span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Sidebar;